import React, { useEffect, useMemo, useState } from 'react';
import { Link, Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { API_BASE } from '../utils/api';
import { useAuth } from '../contexts/AuthContext';
import PasswordFields from '../components/PasswordFields';
import FormField from '../components/FormField';
import { analyzePassword, passwordsMatch } from '../utils/passwordUtils';
import { validateDisplayName } from '../utils/formValidation';

const Signup = () => {
  const [params] = useSearchParams();
  const token = params.get('token');
  const { isAuthenticated, setSession } = useAuth();
  const navigate = useNavigate();
  const [invite, setInvite] = useState(null);
  const [password, setPassword] = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');
  const [name, setName] = useState('');
  const [nameTouched, setNameTouched] = useState(false);
  const [loading, setLoading] = useState(false);
  const [validating, setValidating] = useState(true);

  const nameResult = validateDisplayName(name);
  const passwordAnalysis = useMemo(
    () => analyzePassword(password, invite?.email || ''),
    [password, invite?.email]
  );
  const canSubmit =
    nameResult.valid && passwordAnalysis.isValid && passwordsMatch(password, passwordConfirm);

  useEffect(() => {
    if (!token) {
      setValidating(false);
      return;
    }
    fetch(`${API_BASE}/auth/vendor/validate-signup-token`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token }),
    })
      .then((r) => r.json())
      .then((data) => {
        if (data.success) {
          setInvite(data.data);
          setName(data.data.display_name || '');
        } else {
          toast.error(data.message || 'Invalid invite');
        }
      })
      .catch(() => toast.error('Failed to validate invite'))
      .finally(() => setValidating(false));
  }, [token]);

  if (isAuthenticated) return <Navigate to="/onboarding" replace />;
  if (validating) return <p className="p-8 text-center">Validating invite…</p>;
  if (!token || !invite) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-md p-8 max-w-md text-center">
          <p className="text-slate-700">Invalid or expired invite link.</p>
          <Link to="/login" className="text-teal-700 mt-4 inline-block hover:underline">
            Go to sign in
          </Link>
        </div>
      </div>
    );
  }

  const submit = async (e) => {
    e.preventDefault();
    setNameTouched(true);
    if (!canSubmit) {
      toast.error(nameResult.error || 'Please meet all requirements');
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/auth/vendor/complete-signup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, password, name: nameResult.value }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Signup failed');
      setSession(data.data.token, { ...data.data.user, onboarding_status: 'draft' });
      toast.success('Account created');
      navigate('/onboarding');
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
      <form onSubmit={submit} className="bg-white rounded-xl shadow-md w-full max-w-lg p-8">
        <h1 className="text-2xl font-semibold text-teal-800">Complete signup</h1>
        <p className="text-slate-600 text-sm mt-1 mb-6">
          Invited as <strong>{invite.email}</strong>
        </p>
        <FormField
          id="name"
          label="Your name"
          hint="How we'll address you in the portal"
          value={name}
          onChange={(e) => setName(e.target.value)}
          onBlur={() => setNameTouched(true)}
          maxLength={100}
          autoComplete="name"
          error={nameResult.error}
          valid={nameResult.valid}
          touched={nameTouched}
        />
        <PasswordFields
          password={password}
          confirmPassword={passwordConfirm}
          email={invite.email}
          onPasswordChange={setPassword}
          onConfirmChange={setPasswordConfirm}
        />
        <button
          type="submit"
          disabled={loading || !canSubmit}
          className="w-full mt-6 bg-teal-700 text-white py-2.5 rounded-lg hover:bg-teal-800 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {loading ? 'Creating account…' : 'Create account'}
        </button>
      </form>
    </div>
  );
};

export default Signup;
